import type {
  ChatMessage,
  ChatMessageContent,
  ToolCallProgress,
} from "schemas/generated/chatMessage";

import React, { createContext } from "react";

import { useRequiredContext } from "replo-utils/react/context";
import { useShallowStore } from "replo-utils/react/zustand";
import { createStore } from "zustand";

interface StreamingState {
  isStreaming: boolean;
}

interface ActiveTriggerJobState {
  jobId: string;
  jobType: string;
}

interface ChatStore {
  // NOTE (Bobby, 2025-09-11): All these map chatId to a specific chat's state. Tool call progress may not need to be mapped to a chatId
  // but worth keeping it mapped to chatId since we don't really know how unique toolCallIds are as generated by the LLM providers. Don't want
  // to encounter a weird state where there's a collision of toolCallIds across chats so it's easier to just map it to a chat as well.
  streamingStates: Record<string, StreamingState>;
  activeTriggerJobs: Record<string, ActiveTriggerJobState[]>;
  chatMessages: Record<string, ChatMessage[]>;
  chatToolCallProgress: Record<string, Record<string, ToolCallProgress>>;
  // NOTE (Max, 2025-11-26): For the active chat, we store the active chat turn id (initiated when streaming begins, on the frontend)
  chatTurnIds: Record<string, string>;

  actions: {
    clearMessages: () => void;
    setStreamingState: (chatId: string, state: StreamingState) => void;
    getStreamingState: (chatId: string) => StreamingState;
    addOrUpdateMessageForChat: (
      chatId: string,
      messageId: string,
      content: ChatMessageContent,
      role: "assistant" | "tool" | "user" | "system",
      chatTurnId: string,
    ) => void;
    updateToolCallProgressForChat: (
      chatId: string,
      toolCallId: string,
      update: ToolCallProgress,
    ) => void;
    syncMessagesForChat: (
      chatId: string,
      serverMessages: ChatMessage[],
    ) => void;
    updateStreamingStatus: (chatId: string, isStreaming: boolean) => void;
    addActiveTriggerJob: (chatId: string, job: ActiveTriggerJobState) => void;
    removeActiveTriggerJob: (chatId: string, jobId: string) => void;
    resetChatStore: () => void;
  };
}

const createChatStore = () => {
  return createStore<ChatStore>((set, get) => ({
    streamingStates: {},
    chatMessages: {},
    chatToolCallProgress: {},
    activeTriggerJobs: {},
    chatTurnIds: {},
    actions: {
      setStreamingState: (chatId: string, streamingState: StreamingState) => {
        return set((state) => ({
          streamingStates: {
            ...state.streamingStates,
            [chatId]: streamingState,
          },
        }));
      },

      getStreamingState: (chatId: string) => {
        return get().streamingStates[chatId] ?? { isStreaming: false };
      },

      addOrUpdateMessageForChat: (
        chatId,
        messageId,
        content,
        role,
        chatTurnId,
      ) => {
        return set((state) => {
          const currentMessages = state.chatMessages[chatId] ?? [];
          const existingMessage = currentMessages.find(
            (msg) => msg.id === messageId,
          );

          let updatedMessages;
          if (existingMessage) {
            updatedMessages = currentMessages.map((msg) => {
              return msg.id === messageId
                ? { ...msg, content, createdAt: new Date() }
                : msg;
            });
          } else {
            updatedMessages = [
              ...currentMessages,
              {
                id: messageId,
                content,
                createdAt: new Date(),
                feedback: "none" as const,
                role,
                chatId,
                chatTurnId,
              },
            ];
          }

          return {
            chatMessages: {
              ...state.chatMessages,
              [chatId]: updatedMessages,
            },
          };
        });
      },

      updateToolCallProgressForChat: (chatId, toolCallId, progress) => {
        return set((state) => {
          const updatedProgress = {
            ...state.chatToolCallProgress[chatId],
            [toolCallId]: progress,
          };

          return {
            chatToolCallProgress: {
              ...state.chatToolCallProgress,
              [chatId]: updatedProgress,
            },
          };
        });
      },

      syncMessagesForChat: (chatId: string, serverMessages: ChatMessage[]) => {
        return set((state) => {
          const currentLocalMessages = state.chatMessages[chatId] ?? [];
          const serverMessageIds = new Set(serverMessages.map((msg) => msg.id));
          const localOnlyMessages = currentLocalMessages.filter(
            (msg) => !serverMessageIds.has(msg.id),
          );
          const mergedMessages = [...localOnlyMessages, ...serverMessages].sort(
            (messageA, messageB) => {
              return (
                new Date(messageA.createdAt).getTime() -
                new Date(messageB.createdAt).getTime()
              );
            },
          );

          return {
            chatMessages: {
              ...state.chatMessages,
              [chatId]: mergedMessages,
            },
          };
        });
      },

      updateStreamingStatus: (chatId: string, isStreaming: boolean) => {
        return set((state) => {
          const currentState = state.streamingStates[chatId] ?? {
            isStreaming: false,
          };
          return {
            streamingStates: {
              ...state.streamingStates,
              [chatId]: {
                ...currentState,
                isStreaming,
              },
            },
          };
        });
      },

      clearMessages: () => set({ chatMessages: {} }),
      resetChatStore: () => {
        return set({
          streamingStates: {},
          chatMessages: {},
          chatToolCallProgress: {},
        });
      },

      addActiveTriggerJob: (chatId: string, job: ActiveTriggerJobState) => {
        return set((state) => ({
          activeTriggerJobs: {
            ...state.activeTriggerJobs,
            [chatId]: [...(state.activeTriggerJobs[chatId] ?? []), job],
          },
        }));
      },

      removeActiveTriggerJob: (chatId: string, jobId: string) => {
        return set((state) => {
          const updatedJobs = (state.activeTriggerJobs[chatId] ?? []).filter(
            (job) => job.jobId !== jobId,
          );
          return {
            activeTriggerJobs: {
              ...state.activeTriggerJobs,
              [chatId]: updatedJobs,
            },
          };
        });
      },
    },
  }));
};

type ChatContextType = {
  store: ReturnType<typeof createChatStore>;
};

const ChatContext = createContext<ChatContextType | null>(null);

const useChatContext = () => {
  return useRequiredContext(ChatContext);
};

export function ChatProvider({ children }: React.PropsWithChildren) {
  const [store] = React.useState(() => createChatStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

// Main hook to access the store
function useChatStore<SelectorReturnType>(
  selector: (state: ChatStore) => SelectorReturnType,
): SelectorReturnType {
  const { store } = useChatContext();
  return useShallowStore(store, selector);
}

export const useMessagesForChat = (chatId: string | null) => {
  return useChatStore((state) => {
    if (!chatId) {
      return [];
    }
    return state.chatMessages[chatId] ?? [];
  });
};

export const useIsChatLoading = (chatId: string | null) => {
  return useChatStore((state) => {
    if (!chatId) {
      return false;
    }
    return state.streamingStates[chatId]?.isStreaming ?? false;
  });
};

export const useToolCallProgressForChat = (
  chatId: string | null,
  toolCallId: string,
) => {
  return useChatStore((state) => {
    if (!chatId) {
      return undefined;
    }
    return state.chatToolCallProgress[chatId]?.[toolCallId];
  });
};

export const useChatStoreActions = () => {
  return useChatStore((state) => state.actions);
};

export const useStreamingChats = () => {
  return useChatStore((state) => {
    return Object.entries(state.streamingStates)
      .filter(([, streamingState]) => streamingState.isStreaming)
      .map(([chatId]) => chatId);
  });
};

export const useActiveTriggerJobChats = () => {
  return useChatStore((state) => {
    return Object.entries(state.activeTriggerJobs)
      .filter(([, activeTriggerJobs]) => activeTriggerJobs.length > 0)
      .map(([chatId]) => chatId);
  });
};

export const useActiveTriggerJobsForChat = (chatId: string | null) => {
  return useChatStore((state) => {
    if (!chatId) {
      return [];
    }
    return state.activeTriggerJobs[chatId] ?? [];
  });
};
